/**
 * Tier-1 locales for /:locale/* routing and per-locale prerender.
 *
 * Stable BCP 47 tags. Order matters: en first (canonical / x-default),
 * the rest by descending Google Search Console impressions on the
 * London Underground keyword cluster (top-of-list locales get most
 * sitemap weight). The list is the single source of truth for:
 *
 * - `src/App.tsx` route pattern `/:locale(en|ru|de|…)`
 * - `<Helmet>` hreflang alternates (`PageMeta`, 87 + x-default by
 *   default; the station DETAIL surface narrows the cluster to
 *   `SiteUrls.DETAIL_LOCALES` — see `PageMeta.hreflangLocales`)
 * - `scripts/generate-sitemap.mjs` URL multiplication
 * - `scripts/prerender-locale-helper.mjs` per-locale output dirs
 * - nginx Accept-Language → locale-prefix redirect map
 * - i18n http-backend loadPath (`/locales/{lng}/translation.json`)
 *
 * Keep this list and `public/locales/<tag>/translation.json` in lockstep:
 * adding a tag here without a catalog → 404 on i18n fetch.
 *
 * iter-1 (TASK-localization PR): 39 locales scaffolded, 87 since plan-A1;
 * non-en catalogs
 * are en-source pinned until SEO agent briefs + linguist review land
 * (iter-2 PR, see PM brief). x-default points to en.
 */

export const SUPPORTED_LOCALES = [
  'en',
  'am',
  'ar',
  'as',
  'az',
  'be',
  'bg',
  'bn',
  'ca',
  'cs',
  'da',
  'de',
  'el',
  'en-AU',
  'en-CA',
  'en-GB',
  'en-IN',
  'en-US',
  'es',
  'es-419',
  'es-ES',
  'es-MX',
  'et',
  'fa',
  'fi',
  'fil',
  'fr',
  'fr-CA',
  'fr-FR',
  'gu',
  'ha',
  'he',
  'hi',
  'hr',
  'hu',
  'id',
  'ig',
  'it',
  'ja',
  'jv',
  'kk',
  'km',
  'kn',
  'ko',
  'ky',
  'lt',
  'lv',
  'mg',
  'ml',
  'mr',
  'ms',
  'my',
  'nb',
  'ne',
  'nl',
  'ny',
  'om',
  'or',
  'pa',
  'pl',
  'ps',
  'pt',
  'pt-BR',
  'ro',
  'ru',
  'sd',
  'sh',
  'sk',
  'sl',
  'so',
  'su',
  'sv',
  'sw',
  'ta',
  'te',
  'th',
  'tr',
  'uk',
  'ur',
  'uz',
  'vi',
  'yo',
  'yue',
  'zh',
  'zh-HK',
  'zh-Hans',
  'zh-Hant',
] as const

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

/** en is the canonical / x-default locale. */
export const DEFAULT_LOCALE: SupportedLocale = 'en'

/**
 * RTL locales — drives `<html dir>` attribute in prerendered HTML and at
 * runtime, plus CSS logical-property fallbacks. Only Hebrew + Arabic are
 * RTL in the tier-1 set today; expand as Urdu / Persian land later.
 */
export const RTL_LOCALES: ReadonlySet<SupportedLocale> = new Set(['ar', 'he', 'fa', 'ur', 'ps'])

export function isSupportedLocale(value: string): value is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

export function isRtlLocale(locale: SupportedLocale): boolean {
  return RTL_LOCALES.has(locale)
}

/**
 * Maps a raw browser `navigator.language` / `Accept-Language` token to a
 * supported locale. Resolution order:
 *
 * 1. Exact match on the canonical tag (`en-GB` not in list → next step).
 * 2. Special-case script/region remaps (`zh-CN` → `zh-Hans`, `nb` → `no`).
 * 3. Language-only match (`fr-FR` → `fr`).
 * 4. Fallback to {@link DEFAULT_LOCALE}.
 *
 * Tolerates case (`en-gb` ≡ `en-GB`) and BCP 47 underscore/hyphen variants.
 */
export function resolveSupportedLocale(language: string | null | undefined): SupportedLocale {
  if (language === null || language === undefined || language.length === 0) {
    return DEFAULT_LOCALE
  }

  const parts = language.split(/[-_]/)
  const lang = parts[0].toLowerCase()
  let subtag: string | null = null
  if (parts.length >= 2) {
    const s = parts[1]
    subtag = s.length === 4 ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s.toUpperCase()
  }
  const canonical = subtag !== null ? `${lang}-${subtag}` : lang

  if (isSupportedLocale(canonical)) {
    return canonical
  }

  // Chinese script/region remap — browsers send `zh-CN` / `zh-TW`, our
  // catalogs key on `zh-Hans` / `zh-Hant` (writing-system aligned with
  // SEO keyword surfaces in CN/SG vs TW/HK).
  if (lang === 'zh') {
    if (subtag === 'CN' || subtag === 'SG' || subtag === 'Hans') {
      return 'zh-Hans'
    }
    if (subtag === 'TW' || subtag === 'MO' || subtag === 'HK' || subtag === 'Hant') {
      return 'zh-Hant'
    }
  }

  // Norwegian: nn → nb (Bokmål canonical, aligned with iOS xcstrings + AOSP).
  if (lang === 'nn') {
    return 'nb'
  }

  // Portuguese: only Brazil gets its own catalog; generic `pt` (Portugal +
  // other) falls back to the `pt` entry.
  if (lang === 'pt') {
    if (subtag === 'BR') {
      return 'pt-BR'
    }
    return 'pt'
  }

  // Serbo-Croatian umbrella: bs → sh (canonical, aligned with iOS xcstrings).
  if (lang === 'bs' || lang === 'sr') {
    return 'sh'
  }

  if (isSupportedLocale(lang)) {
    return lang
  }

  return DEFAULT_LOCALE
}
